import CalendarTodayRoundedIcon from "@mui/icons-material/CalendarTodayRounded";
import CodeIcon from "@mui/icons-material/Code";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import GlobalStyles from "@mui/material/GlobalStyles";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import MenuItem from "@mui/material/MenuItem";
import Popover from "@mui/material/Popover";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import FileSaver from "file-saver";
import moment from "moment";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { enUS } from "date-fns/locale";
import { DateRange } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

import { GlobalState } from "../../../context/GlobalContext";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import ResultTable from "../ResultTable";

const runButtonSx = {
	width: "fit-content",
	minWidth: 148,
	textTransform: "none",
	fontWeight: 600,
	borderRadius: 1.5,
	bgcolor: "#1f2937",
	px: 2.25,
	boxShadow: "none",
	"&:hover": { bgcolor: "#111827", boxShadow: "none" },
};

const downloadButtonSx = {
	textTransform: "none",
	fontWeight: 600,
	borderRadius: 1.5,
	borderColor: "#d1d5db",
	color: "#374151",
	boxShadow: "none",
	"&:hover": { borderColor: "#9ca3af", bgcolor: "#f9fafb", boxShadow: "none" },
};

export const compareOptions = [
	{
		value: "uc_vs_ginesys",
		label: "UC vs Ginesys",
		hint: "Records in Unicommerce that are missing in Ginesys",
	},
	{
		value: "ginesys_vs_uc",
		label: "Ginesys vs UC",
		hint: "Records in Ginesys that are missing in Unicommerce",
	},
];

const subTabSx = {
	px: { xs: 1, md: 2 },
	minHeight: 44,
	borderBottom: "1px solid #eef0f3",
	"& .MuiTab-root": {
		textTransform: "none",
		fontWeight: 600,
		fontSize: "0.875rem",
		minHeight: 44,
		color: "#6b7280",
		"&.Mui-selected": { color: "#111827" },
	},
	"& .MuiTabs-indicator": { bgcolor: "#111827", height: 2 },
};

const csvCell = (value) => {
	if (value === null || value === undefined) return "";
	const text = typeof value === "object" ? JSON.stringify(value) : String(value);
	return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

function toCsv(rows) {
	const columns = [];
	const seen = new Set();
	rows.forEach((row) =>
		Object.keys(row).forEach((key) => {
			if (!seen.has(key)) {
				seen.add(key);
				columns.push(key);
			}
		})
	);
	const lines = [columns.map(csvCell).join(",")];
	rows.forEach((row) => lines.push(columns.map((column) => csvCell(row[column])).join(",")));
	return `\uFEFF${lines.join("\r\n")}`;
}

function uniqueSorted(values) {
	return [...new Set((values || []).filter(Boolean))].sort((a, b) => a.localeCompare(b));
}

function isEveryFacility(list, options) {
	return !list?.length || (options.length > 0 && list.length === options.length);
}

function labelForCompare(labels, compare) {
	if (!labels) return "";
	if (typeof labels === "string") return labels;
	return labels[compare] || "";
}

function describeFacilities(list, options) {
	if (isEveryFacility(list, options)) return "All facilities";
	if (list.length === 1) return list[0];
	return `${list.length} facilities`;
}

function useFacilities(facilitiesEndpoint, compare, active = true) {
	const [facilities, setFacilities] = useState([]);
	const [ucFacilities, setUcFacilities] = useState([]);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		if (!active) return undefined;
		let cancelled = false;
		setFacilities([]);
		setUcFacilities([]);
		setLoading(true);
		pythonVmInstance({ url: facilitiesEndpoint, params: { compare }, method: "GET" })
			.then((res) => {
				if (cancelled) return;
				setFacilities(uniqueSorted(res?.data?.result));
				setUcFacilities(uniqueSorted(res?.data?.ucFacilities));
			})
			.catch(() => {
				if (!cancelled) {
					setFacilities([]);
					setUcFacilities([]);
				}
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [facilitiesEndpoint, compare, active]);

	return { facilities, ucFacilities, facilitiesLoading: loading };
}

const FACILITY_MATCH_LIMIT = 40;

const FacilitySearchInput = memo(function FacilitySearchInput({ optionCount, onFilter, resetSignal }) {
	const [text, setText] = useState("");
	const timer = useRef(null);

	useEffect(() => {
		setText("");
		clearTimeout(timer.current);
	}, [resetSignal]);

	useEffect(() => () => clearTimeout(timer.current), []);

	return (
		<TextField
			size="small"
			autoFocus
			fullWidth
			placeholder={`Search ${optionCount} facilities`}
			value={text}
			onChange={(event) => {
				const next = event.target.value;
				setText(next);
				clearTimeout(timer.current);
				timer.current = setTimeout(() => onFilter(next), 80);
			}}
			onKeyDown={(event) => {
				if (event.key !== "Escape") event.stopPropagation();
			}}
			InputProps={{
				startAdornment: (
					<InputAdornment position="start">
						<SearchRoundedIcon sx={{ fontSize: 18, color: "#9ca3af" }} />
					</InputAdornment>
				),
			}}
			sx={{
				"& .MuiOutlinedInput-root": { borderRadius: 1.5, fontSize: "0.875rem" },
				"& fieldset": { borderColor: "#e5e7eb" },
			}}
		/>
	);
});

function FacilityMultiSelect({ label, hint, options, value, onChange, loading, wide }) {
	const [search, setSearch] = useState("");
	const [searchReset, setSearchReset] = useState(0);
	const onFilter = useCallback((next) => setSearch(next), []);
	const indexedOptions = useMemo(() => options.map((name) => ({ name, lower: name.toLowerCase() })), [options]);
	const selectedSet = useMemo(() => new Set(value), [value]);
	const searchTerm = search.trim().toLowerCase();
	const matches = useMemo(() => {
		if (!searchTerm) return options;
		const found = [];
		for (let index = 0; index < indexedOptions.length; index += 1) {
			if (indexedOptions[index].lower.includes(searchTerm)) found.push(indexedOptions[index].name);
		}
		return found;
	}, [indexedOptions, options, searchTerm]);
	const visible = matches.length > FACILITY_MATCH_LIMIT ? matches.slice(0, FACILITY_MATCH_LIMIT) : matches;
	const allSelected = options.length > 0 && value.length === options.length;
	const someSelected = value.length > 0 && !allSelected;
	let visibleSelectedCount = 0;
	for (let index = 0; index < matches.length; index += 1) {
		if (selectedSet.has(matches[index])) visibleSelectedCount += 1;
	}
	const allVisibleSelected = matches.length > 0 && visibleSelectedCount === matches.length;
	const someVisibleSelected = visibleSelectedCount > 0 && !allVisibleSelected;

	const handleChange = (event) => {
		const raw = event.target.value;
		const next = typeof raw === "string" ? raw.split(",") : raw;
		if (next.includes("__all__")) {
			if (allVisibleSelected) onChange(value.filter((name) => !matches.includes(name)));
			else onChange([...new Set([...value, ...matches])]);
			return;
		}
		onChange(next.filter((name) => name !== undefined));
	};

	return (
		<Stack spacing={0.75}>
			<InputLabel sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#6b7280" }}>
				{label}
				{hint && (
					<Box component="span" sx={{ fontWeight: 500, color: "#9ca3af" }}>
						{` · ${hint}`}
					</Box>
				)}
			</InputLabel>
			<Select
				multiple
				size="small"
				value={value}
				onChange={handleChange}
				displayEmpty
				disabled={loading || !options.length}
				renderValue={(selected) =>
					loading ? "Loading facilities..." : selected.length ? describeFacilities(selected, options) : "Select facilities"
				}
				onClose={() => {
					setSearch("");
					setSearchReset((current) => current + 1);
				}}
				MenuProps={{
					autoFocus: false,
					disableAutoFocus: true,
					disableAutoFocusItem: true,
					disableEnforceFocus: true,
					PaperProps: { sx: { maxHeight: 420 } },
				}}
				sx={{
					height: 40,
					width: { xs: "100%", sm: wide ? 360 : 260 },
					borderRadius: 1.5,
					bgcolor: "#fff",
					fontSize: "0.875rem",
					fontWeight: 600,
					color: value.length && !allSelected ? "#111827" : "#6b7280",
					"& fieldset": { borderColor: "#e5e7eb" },
					"&:hover fieldset": { borderColor: "#d1d5db" },
					"&.Mui-focused fieldset": { borderColor: "#6b7280", borderWidth: 1 },
				}}>
				<ListSubheader sx={{ px: 1.5, pt: 1.5, pb: 1, bgcolor: "#fff", lineHeight: "normal" }}>
					<FacilitySearchInput optionCount={options.length} onFilter={onFilter} resetSignal={searchReset} />
				</ListSubheader>
				{matches.length ? (
					<MenuItem value="__all__" dense>
						<Checkbox
							size="small"
							checked={searchTerm ? allVisibleSelected : allSelected}
							indeterminate={searchTerm ? someVisibleSelected : someSelected}
							sx={{ p: 0.5, mr: 1 }}
						/>
						<ListItemText
							primary={searchTerm ? `Select all matching (${matches.length})` : "All facilities"}
							primaryTypographyProps={{ fontSize: "0.875rem", fontWeight: 600 }}
						/>
					</MenuItem>
				) : (
					<MenuItem disabled dense>
						<ListItemText
							primary={`No facilities match "${search.trim()}"`}
							primaryTypographyProps={{ fontSize: "0.875rem" }}
						/>
					</MenuItem>
				)}
				{visible.map((name) => (
					<MenuItem key={name} value={name} dense>
						<Checkbox size="small" checked={selectedSet.has(name)} sx={{ p: 0.5, mr: 1 }} />
						<ListItemText primary={name} primaryTypographyProps={{ fontSize: "0.875rem" }} />
					</MenuItem>
				))}
				{matches.length > visible.length && (
					<MenuItem disabled dense>
						<ListItemText
							primary={`Showing ${visible.length} of ${matches.length}. Keep typing to narrow.`}
							primaryTypographyProps={{ fontSize: "0.75rem", color: "#6b7280" }}
						/>
					</MenuItem>
				)}
			</Select>
		</Stack>
	);
}

export function RangeQuery({ title, endpoint, facilitiesEndpoint, facilityLabels, ucFacilityLabel, filePrefix, directions = compareOptions, active = true }) {
	const { setSnackBar, snackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();
	const [result, setResult] = useState([]);
	const [hasRun, setHasRun] = useState(false);
	const [tableView, setTableView] = useState({ rows: null, query: "" });
	const [loadedRange, setLoadedRange] = useState({ startDate: "", endDate: "" });
	const [startDate, setStartDate] = useState("");
	const [endDate, setEndDate] = useState("");
	const [facility, setFacility] = useState([]);
	const [ucFacility, setUcFacility] = useState([]);
	const [loadedFacility, setLoadedFacility] = useState([]);
	const [loadedUcFacility, setLoadedUcFacility] = useState([]);
	const [loadedShowUc, setLoadedShowUc] = useState(false);
	const [rangeAnchor, setRangeAnchor] = useState(null);
	const [draftRange, setDraftRange] = useState({
		startDate: new Date(),
		endDate: new Date(),
		key: "selection",
	});
	const [compare, setCompare] = useState(compareOptions[0].value);
	const [loadedCompare, setLoadedCompare] = useState(compareOptions[0].value);
	const { facilities, ucFacilities, facilitiesLoading } = useFacilities(facilitiesEndpoint, compare, active);
	const ucLabel = labelForCompare(ucFacilityLabel, compare);
	const showUcFacility = Boolean(ucLabel);

	useEffect(() => {
		setFacility([]);
	}, [facilities]);

	useEffect(() => {
		setUcFacility([]);
	}, [ucFacilities]);

	const selectedCompare = compareOptions.find((option) => option.value === compare) || compareOptions[0];
	const loadedOption = compareOptions.find((option) => option.value === loadedCompare) || compareOptions[0];
	const wideFacilitySelect = /ginesys|\bgn\b/i.test(facilityLabels?.[compare] || "");
	const gnFacilityFirst = compare === "ginesys_vs_uc";
	const loadedGnFirst = loadedCompare === "ginesys_vs_uc";
	const ucSummary = `${labelForCompare(ucFacilityLabel, loadedCompare) || "UC facility"}: ${describeFacilities(loadedUcFacility, ucFacilities)}`;
	const gnSummary = `${facilityLabels?.[loadedCompare] || "GN facility"}: ${describeFacilities(loadedFacility, facilities)}`;
	const filterSummary = loadedShowUc
		? loadedGnFirst
			? `${gnSummary} · ${ucSummary}`
			: `${ucSummary} · ${gnSummary}`
		: describeFacilities(loadedFacility, facilities);

	const openRange = (event) => {
		const start = startDate ? moment(startDate).toDate() : new Date();
		const end = endDate ? moment(endDate).toDate() : start;
		setDraftRange({ startDate: start, endDate: end, key: "selection" });
		setRangeAnchor(event.currentTarget);
	};

	const applyRange = () => {
		const start = moment(draftRange.startDate);
		const end = moment(draftRange.endDate);
		const from = (start.isAfter(end) ? end : start).format("YYYY-MM-DD");
		const to = (start.isAfter(end) ? start : end).format("YYYY-MM-DD");
		setStartDate(from);
		setEndDate(to);
		setRangeAnchor(null);
	};

	const runQuery = async (event) => {
		event?.preventDefault?.();
		const from = startDate;
		const to = endDate;
		const facilityValue = facility;
		if (!from || !to) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Select a start and end date",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			return;
		}
		try {
			setLoadingOpen(true);
			const params = { startDate: from, endDate: to, action: "get", compare };
			if (facilityValue.length && !isEveryFacility(facilityValue, facilities)) params.facility = facilityValue.join(",");
			if (showUcFacility && ucFacility.length && !isEveryFacility(ucFacility, ucFacilities)) {
				params.ucFacility = ucFacility.join(",");
			}
			const response = await pythonVmInstance({
				url: endpoint,
				params,
				headers: { "Content-Type": "application/json" },
				method: "GET",
			});

			const data = response?.data?.result;
			setResult(Array.isArray(data) ? data : []);
			setHasRun(true);
			setLoadedRange({ startDate: from, endDate: to });
			setLoadedCompare(compare);
			setLoadedFacility(facilityValue);
			setLoadedUcFacility(showUcFacility ? ucFacility : []);
			setLoadedShowUc(showUcFacility);
			setLoadingOpen(false);
		} catch (error) {
			setLoadingOpen(false);
			setResult([]);
			setHasRun(false);
			const message = error?.response?.data?.message || "Failed to load the query";
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message,
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		}
	};

	const downloadLoaded = () => {
		const facilitySuffix = isEveryFacility(loadedFacility, facilities)
			? ""
			: loadedFacility.length === 1
			? `_${loadedFacility[0].replace(/[^\w-]+/g, "_")}`
			: `_${loadedFacility.length}_facilities`;
		const ucSuffix =
			!loadedShowUc || isEveryFacility(loadedUcFacility, ucFacilities)
				? ""
				: loadedUcFacility.length === 1
				? `_uc_${loadedUcFacility[0].replace(/[^\w-]+/g, "_")}`
				: `_uc_${loadedUcFacility.length}_facilities`;
		const searchSuffix = tableView.query ? `_search_${tableView.query.replace(/[^\w-]+/g, "_").slice(0, 40)}` : "";
		const blob = new Blob([toCsv(tableView.rows || result)], { type: "text/csv;charset=utf-8" });
		FileSaver.saveAs(
			blob,
			`${loadedRange.startDate}_${loadedRange.endDate}_${filePrefix}_${loadedCompare}${facilitySuffix}${ucSuffix}${searchSuffix}.csv`
		);
	};

	const handleCompareChange = (_, value) => {
		if (!value || value === compare) return;
		setCompare(value);
		setResult([]);
		setHasRun(false);
		setFacility([]);
		setUcFacility([]);
	};

	return (
		<Stack spacing={2.5} component="form" onSubmit={runQuery}>
			<Box
				sx={{
					border: "1px solid #e6e8ec",
					borderRadius: 2,
					bgcolor: "#fafbfc",
					p: { xs: 1.5, sm: 2 },
				}}>
				<Typography sx={{ fontSize: "0.75rem", fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "#9ca3af", mb: 1 }}>
					{title} direction
				</Typography>
				<Box
					sx={{
						display: "flex",
						flexWrap: "wrap",
						gap: 0.5,
						p: 0.5,
						width: "fit-content",
						maxWidth: "100%",
						bgcolor: "#eceff3",
						borderRadius: 2,
					}}>
					{directions.map((option) => {
						const selected = compare === option.value;
						return (
							<Box
								key={option.value}
								component="button"
								type="button"
								onClick={() => handleCompareChange(null, option.value)}
								sx={{
									border: 0,
									cursor: "pointer",
									borderRadius: 1.5,
									px: 2,
									py: 1,
									fontFamily: "inherit",
									fontWeight: 600,
									fontSize: "0.875rem",
									lineHeight: 1.2,
									bgcolor: selected ? "#111827" : "transparent",
									color: selected ? "#fff" : "#4b5563",
									boxShadow: selected ? "0 1px 2px rgba(17,24,39,0.18)" : "none",
									"&:hover": { bgcolor: selected ? "#1f2937" : "#e5e7eb" },
								}}>
								{option.label}
							</Box>
						);
					})}
				</Box>
				<Typography variant="body2" color="text.secondary" sx={{ mt: 1.25 }}>
					{selectedCompare.hint}
				</Typography>

				<Box display="flex" columnGap={1.5} rowGap={1.5} flexWrap="wrap" alignItems="flex-end" mt={2}>
					{(gnFacilityFirst
						? ["gn", "uc"]
						: ["uc", "gn"]
					).map((kind) =>
						kind === "uc" ? (
							showUcFacility ? (
								<FacilityMultiSelect
									key="uc-facility"
									label={ucLabel}
									options={ucFacilities}
									value={ucFacility}
									onChange={setUcFacility}
									loading={facilitiesLoading}
								/>
							) : null
						) : (
							<FacilityMultiSelect
								key="gn-facility"
								label={showUcFacility ? facilityLabels?.[compare] || "GN facility" : "Facility"}
								hint={showUcFacility ? "" : facilityLabels?.[compare]}
								options={facilities}
								value={facility}
								onChange={setFacility}
								loading={facilitiesLoading}
								wide={wideFacilitySelect || compare === "ginesys_vs_uc"}
							/>
						)
					)}
					<Stack spacing={0.75}>
						<InputLabel sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#6b7280" }}>Date range</InputLabel>
						<Button
							type="button"
							variant="outlined"
							onClick={openRange}
							startIcon={<CalendarTodayRoundedIcon sx={{ fontSize: 18 }} />}
							sx={{
								height: 40,
								minWidth: { xs: "100%", sm: 280 },
								justifyContent: "flex-start",
								textTransform: "none",
								fontWeight: 600,
								borderRadius: 1.5,
								borderColor: "#e5e7eb",
								color: startDate && endDate ? "#111827" : "#6b7280",
								bgcolor: "#fff",
								"&:hover": { borderColor: "#d1d5db", bgcolor: "#fff" },
							}}>
							{startDate && endDate
								? `${moment(startDate).format("DD MMM YYYY")} – ${moment(endDate).format("DD MMM YYYY")}`
								: "Select start and end date"}
						</Button>
						<Popover
							open={Boolean(rangeAnchor)}
							anchorEl={rangeAnchor}
							onClose={() => setRangeAnchor(null)}
							anchorOrigin={{ vertical: "bottom", horizontal: "left" }}>
							<GlobalStyles
								styles={{
									".tir-date-range .rdrDayNumber span": { color: "#111827" },
									".tir-date-range .rdrDayPassive .rdrDayNumber span": { color: "#d1d5db" },
									".tir-date-range .rdrStartEdge, .tir-date-range .rdrEndEdge, .tir-date-range .rdrInRange": {
										background: "#111827 !important",
									},
									".tir-date-range .rdrStartEdge ~ .rdrDayNumber span, .tir-date-range .rdrEndEdge ~ .rdrDayNumber span, .tir-date-range .rdrInRange ~ .rdrDayNumber span":
										{ color: "#fff" },
								}}
							/>
							<Box className="tir-date-range">
							<DateRange
								ranges={[draftRange]}
								onChange={(item) => setDraftRange(item.selection)}
								moveRangeOnFirstSelection={false}
								months={2}
								direction="horizontal"
								showDateDisplay={false}
								locale={enUS}
								maxDate={new Date()}
								rangeColors={["#111827"]}
							/>
							</Box>
							<Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ px: 1.5, py: 1, borderTop: "1px solid #eef0f3" }}>
								<Button type="button" onClick={() => setRangeAnchor(null)} sx={downloadButtonSx} variant="outlined">
									Cancel
								</Button>
								<Button type="button" onClick={applyRange} sx={runButtonSx} variant="contained">
									Apply
								</Button>
							</Stack>
						</Popover>
					</Stack>
					<Button
						variant="contained"
						sx={{ ...runButtonSx, height: 40 }}
						type="submit"
						disabled={facilitiesLoading}
						startIcon={<CodeIcon />}>
						Run Query
					</Button>
				</Box>
			</Box>

			{result.length ? (
				<Box>
					<Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1.5} mb={1.5}>
						<Box>
							<Typography sx={{ fontWeight: 700, color: "#111827", fontSize: "1.05rem" }}>{loadedOption.label}</Typography>
							<Typography variant="body2" color="text.secondary">
								{filterSummary} · {moment(loadedRange.startDate).format("DD MMM YYYY")} to{" "}
								{moment(loadedRange.endDate).format("DD MMM YYYY")} · {result.length.toLocaleString()} rows
							</Typography>
						</Box>
						<Button
							type="button"
							onClick={downloadLoaded}
							disabled={Boolean(tableView.query) && !tableView.rows?.length}
							sx={downloadButtonSx}
							variant="outlined">
							{tableView.query
								? `Download ${(tableView.rows?.length || 0).toLocaleString()} matching rows`
								: "Download"}
						</Button>
					</Stack>
					<ResultTable rows={result} onViewChange={setTableView} />
				</Box>
			) : hasRun ? (
				<Box
					sx={{
						border: "1px dashed #e5e7eb",
						borderRadius: 2,
						py: 5,
						textAlign: "center",
					}}>
					<Typography sx={{ fontWeight: 600, color: "#111827" }}>No records found</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
						{loadedOption.label} · {filterSummary} · {moment(loadedRange.startDate).format("DD MMM YYYY")} to{" "}
						{moment(loadedRange.endDate).format("DD MMM YYYY")}
					</Typography>
				</Box>
			) : null}
		</Stack>
	);
}

const queries = [
	{
		key: "tir",
		label: "TIR",
		title: "TIR",
		endpoint: "/uc_vs_ginesys/tir",
		facilitiesEndpoint: "/uc_vs_ginesys/tir/facilities",
		facilityLabels: { uc_vs_ginesys: "GN facility", ginesys_vs_uc: "GN facility" },
		ucFacilityLabel: "UC facility",
		filePrefix: "tir",
	},
	{
		key: "online-rtv",
		label: "Online RTV",
		title: "Online RTV",
		endpoint: "/uc_vs_ginesys/online-rtv",
		facilitiesEndpoint: "/uc_vs_ginesys/online-rtv/facilities",
		facilityLabels: { uc_vs_ginesys: "GN facility", ginesys_vs_uc: "GN facility" },
		ucFacilityLabel: { uc_vs_ginesys: "UC facility", ginesys_vs_uc: "UC facility" },
		filePrefix: "online_rtv",
	},
];

const Tir = () => {
	const [tab, setTab] = useState(0);

	return (
		<Box>
			<Tabs value={tab} onChange={(_, value) => setTab(value)} sx={subTabSx}>
				{queries.map((query, index) => (
					<Tab
						key={query.key}
						label={query.label}
						id={`tir-subtab-${index}`}
						aria-controls={`tir-subtabpanel-${index}`}
					/>
				))}
			</Tabs>
			{queries.map((query, index) => (
				<Box
					key={query.key}
					role="tabpanel"
					id={`tir-subtabpanel-${index}`}
					aria-labelledby={`tir-subtab-${index}`}
					sx={{ display: tab === index ? "block" : "none", p: { xs: 2, md: 3 } }}>
					<RangeQuery
						title={query.title}
						endpoint={query.endpoint}
						facilitiesEndpoint={query.facilitiesEndpoint}
						facilityLabels={query.facilityLabels}
						ucFacilityLabel={query.ucFacilityLabel}
						directions={query.directions}
						filePrefix={query.filePrefix}
					/>
				</Box>
			))}
		</Box>
	);
};

export default Tir;
